import logger from '../../utils/logger';
import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { 
  Plus, 
  Edit, 
  Trash2, 
  Eye, 
  Search, 
  TrendingUp,
  FileText
} from 'lucide-react';
import blogService from '../../services/blogService';
import toast from 'react-hot-toast';
import Button from '../../components/common/Button';
import Input from '../../components/common/Input';
import Select from '../../components/common/Select';
import Card from '../../components/common/Card';

const BlogManagement = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [blogs, setBlogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [currentPage, setCurrentPage] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [stats, setStats] = useState(null);

  const loadBlogs = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      
      let response;
      if (statusFilter === 'all') {
        response = await blogService.getAllBlogs(currentPage, 10);
      } else {
        response = await blogService.getBlogsByStatus(statusFilter, currentPage, 10);
      }

      if (response.success) {
        setBlogs(response.data);
        setTotalPages(response.pagination.totalPages);
      } else {
        setError(response.error);
      }
    } catch (error) {
      setError(t('admin.failedToLoadBlogs'));
    } finally {
      setLoading(false);
    }
  }, [currentPage, statusFilter, t]);

  const loadStats = useCallback(async () => {
    try {
      const response = await blogService.getBlogStats();
      if (response.success) {
        setStats(response.data);
      }
    } catch (error) {
  logger.error('Failed to load blog stats:', error);
    }
  }, []);

  useEffect(() => {
    loadBlogs();
    loadStats();
  }, [currentPage, statusFilter, loadBlogs, loadStats]);

  const handleDeleteBlog = async (id) => {
    if (window.confirm(t('admin.confirmDeleteBlog'))) {
      try {
        const response = await blogService.deleteBlog(id);
        if (response.success) {
          toast.success(t('admin.blogDeleted'));
          loadBlogs();
          loadStats();
        } else {
          toast.error(response.error);
        }
      } catch (error) {
        toast.error(t('admin.failedToDeleteBlog'));
      }
    }
  };

  const getStatusBadge = (status) => {
    const statusClasses = {
      PUBLISHED: 'bg-green-600 text-white',
      DRAFT: 'bg-yellow-600 text-white',
      ARCHIVED: 'bg-gray-600 text-white'
    };
    
    return (
      <span className={`px-2 py-1 rounded-full text-xs ${statusClasses[status]}`}>
        {t(`admin.blogStatus.${status.toLowerCase()}`)}
      </span>
    );
  };

  const getCategoryLabel = (category) => {
    return t(`blog.categories.${category.toLowerCase()}`, category.replace('_', ' '));
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString();
  };

  if (loading && blogs.length === 0) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-[#1f2937] to-[#111827] flex items-center justify-center">
        <div className="text-center text-white">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#a15507] mx-auto mb-4"></div>
          <p className="text-gray-300">{t('admin.loading')}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#1f2937] to-[#111827] p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-8">
          <div>
            <h1 className="text-2xl md:text-3xl font-bold text-white mb-2">{t('admin.blogManagement')}</h1>
            <p className="text-gray-400 text-sm md:text-base">{t('admin.blogManagementDescription')}</p>
          </div>
          <Button
            onClick={() => navigate('/admin/blog/create')}
            size="sm"
            className="w-full md:w-auto"
          >
            <Plus className="w-4 h-4 mr-2" />
            {t('admin.createBlog')}
          </Button>
        </div>

        {/* Stats Cards */}
        {stats && (
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 md:gap-6 mb-8">
            <Card className="hover:border-gray-600/50 transition-colors">
              <div className="flex items-center justify-between">
                <div className="flex-1">
                  <p className="text-gray-400 text-xs md:text-sm">{t('admin.totalBlogs')}</p>
                  <p className="text-xl md:text-2xl font-bold text-white">{stats.totalBlogs}</p>
                </div>
                <FileText className="w-6 h-6 md:w-8 md:h-8 text-[#a15507] flex-shrink-0" />
              </div>
            </Card>
            <Card className="hover:border-gray-600/50 transition-colors">
              <div className="flex items-center justify-between">
                <div className="flex-1">
                  <p className="text-gray-400 text-xs md:text-sm">{t('admin.publishedBlogs')}</p>
                  <p className="text-xl md:text-2xl font-bold text-white">{stats.publishedBlogs}</p>
                </div>
                <Eye className="w-6 h-6 md:w-8 md:h-8 text-green-600 flex-shrink-0" />
              </div>
            </Card>
            <Card className="hover:border-gray-600/50 transition-colors">
              <div className="flex items-center justify-between">
                <div className="flex-1">
                  <p className="text-gray-400 text-xs md:text-sm">{t('admin.draftBlogs')}</p>
                  <p className="text-xl md:text-2xl font-bold text-white">{stats.draftBlogs}</p>
                </div>
                <Edit className="w-6 h-6 md:w-8 md:h-8 text-yellow-600 flex-shrink-0" />
              </div>
            </Card>
            <Card className="hover:border-gray-600/50 transition-colors">
              <div className="flex items-center justify-between">
                <div className="flex-1">
                  <p className="text-gray-400 text-xs md:text-sm">{t('admin.totalViews')}</p>
                  <p className="text-xl md:text-2xl font-bold text-white">{stats.totalViews}</p>
                </div>
                <TrendingUp className="w-6 h-6 md:w-8 md:h-8 text-blue-600 flex-shrink-0" />
              </div>
            </Card>
          </div>
        )}

        {/* Filters */}
        <Card className="mb-6">
          <div className="flex flex-col md:flex-row gap-4 items-center justify-between">
            <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4 w-full md:w-auto">
              <div className="relative w-full sm:w-auto">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                <Input
                  type="text"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  placeholder={t('admin.searchBlogs')}
                  size="sm"
                  className="pl-10 w-full sm:w-64"
                />
              </div>
              <div className="w-full sm:w-auto">
                <Select
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                  size="sm"
                  className="w-full sm:w-48"
                >
                  <option value="all">{t('admin.allStatus')}</option>
                  <option value="published">{t('admin.blogStatus.published')}</option>
                  <option value="draft">{t('admin.blogStatus.draft')}</option>
                  <option value="archived">{t('admin.blogStatus.archived')}</option>
                </Select>
              </div>
            </div>
          </div>
        </Card>

        {/* Blog List */}
        <div className="card">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-gray-700">
                  <th className="text-left py-3 px-4 text-white font-medium">{t('admin.title')}</th>
                  <th className="text-left py-3 px-4 text-white font-medium">{t('admin.category')}</th>
                  <th className="text-left py-3 px-4 text-white font-medium">{t('admin.status')}</th>
                  <th className="text-left py-3 px-4 text-white font-medium">{t('admin.views')}</th>
                  <th className="text-left py-3 px-4 text-white font-medium">{t('admin.created')}</th>
                  <th className="text-left py-3 px-4 text-white font-medium">{t('admin.actions')}</th>
                </tr>
              </thead>
              <tbody>
                {blogs.map((blog) => (
                  <tr key={blog.id} className="border-b border-gray-800 hover:bg-gray-800/50">
                    <td className="py-4 px-4">
                      <div>
                        <h3 className="text-white font-medium mb-1">{blog.title}</h3>
                        <p className="text-gray-400 text-sm line-clamp-1">{blog.excerpt}</p>
                      </div>
                    </td>
                    <td className="py-4 px-4">
                      <span className="px-2 py-1 bg-[#a15507] text-white text-xs rounded-full">
                        {getCategoryLabel(blog.category)}
                      </span>
                    </td>
                    <td className="py-4 px-4">
                      {getStatusBadge(blog.status)}
                    </td>
                    <td className="py-4 px-4 text-gray-300">
                      {blog.viewCount}
                    </td>
                    <td className="py-4 px-4 text-gray-300">
                      {formatDate(blog.createdAt)}
                    </td>
                    <td className="py-4 px-4">
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => navigate(`/admin/blog/edit/${blog.id}`)}
                          className="p-2 text-blue-400 hover:text-blue-300 transition-colors"
                          title={t('admin.edit')}
                        >
                          <Edit className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleDeleteBlog(blog.id)}
                          className="p-2 text-red-400 hover:text-red-300 transition-colors"
                          title={t('admin.delete')}
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex justify-center items-center gap-2 mt-6 pt-6 border-t border-gray-700">
              <button
                onClick={() => setCurrentPage(currentPage - 1)}
                disabled={currentPage === 0}
                className="px-4 py-2 bg-gray-700 text-white rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-600 transition-colors"
              >
                {t('admin.previous')}
              </button>
              
              {[...Array(totalPages)].map((_, index) => (
                <button
                  key={index}
                  onClick={() => setCurrentPage(index)}
                  className={`px-4 py-2 rounded-lg transition-colors ${
                    currentPage === index
                      ? 'bg-[#a15507] text-white'
                      : 'bg-gray-700 text-white hover:bg-gray-600'
                  }`}
                >
                  {index + 1}
                </button>
              ))}
              
              <button
                onClick={() => setCurrentPage(currentPage + 1)}
                disabled={currentPage === totalPages - 1}
                className="px-4 py-2 bg-gray-700 text-white rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-600 transition-colors"
              >
                {t('admin.next')}
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default BlogManagement;
